import i18next from "i18next";
/**
 * Pure utility functions for DataTable components
 *
 * This file contains utility functions with no React dependencies,
 * making them lightweight and suitable for use in any context.
 */ /**
 * URL State Management
 */ /**
 * Get prefixed parameter name for table-specific URL state
 * @param paramName - Base parameter name (e.g., 'page', 'sort')
 * @param tableId - Optional table identifier for namespacing
 * @returns Prefixed parameter name (e.g., 'assignments_page') or base name if no tableId
 *
 * @example
 * ```ts
 * getPrefixedParamName('page', 'assignments') // "assignments_page"
 * getPrefixedParamName('sort') // "sort"
 * ```
 */ export function getPrefixedParamName(paramName, tableId) {
    return tableId ? `${tableId}_${paramName}` : paramName;
}
/**
 * Get list of all table-related parameter names (both prefixed and unprefixed)
 * Used for cleaning up URL params
 * @param tableId - Optional table identifier
 * @returns Array of parameter names
 */ export function getTableParamNames(tableId) {
    // Only keys the debounced URL sync owns. `${tableId}_q` and chip-filter keys
    // are written directly by DataTable and must pass through unchanged here.
    const baseParams = [
        "sort",
        "page",
        "pageSize",
        "hiddenColumns"
    ];
    return baseParams.map((param)=>getPrefixedParamName(param, tableId));
}
/**
 * Parse sorting from URL param format: "name.asc" or "name.desc"
 * @param sortParam - URL parameter string
 * @returns Sorting state array
 *
 * @example
 * ```ts
 * parseSortingFromUrl("name.asc") // [{ id: "name", desc: false }]
 * parseSortingFromUrl("createdAt.desc") // [{ id: "createdAt", desc: true }]
 * ```
 */ export function parseSortingFromUrl(sortParam) {
    if (!sortParam) return [];
    const [id, direction] = sortParam.split(".");
    return [
        {
            id,
            desc: direction === "desc"
        }
    ];
}
/**
 * Serialize sorting to URL param format: "name.asc"
 * @param sorting - Sorting state array
 * @returns URL parameter string
 *
 * @example
 * ```ts
 * serializeSorting([{ id: "name", desc: false }]) // "name.asc"
 * serializeSorting([{ id: "createdAt", desc: true }]) // "createdAt.desc"
 * ```
 */ export function serializeSorting(sorting) {
    if (sorting.length === 0) return "";
    const { id, desc } = sorting[0];
    return `${id}.${desc ? "desc" : "asc"}`;
}
/**
 * Parse column visibility from URL param format: "address,phone"
 * @param hiddenColumnsParam - Comma-separated list of hidden column IDs
 * @returns ColumnVisibilityState object with hidden columns set to false
 *
 * @example
 * ```ts
 * parseColumnVisibilityFromUrl("address,phone") // { address: false, phone: false }
 * parseColumnVisibilityFromUrl("") // {}
 * ```
 */ export function parseColumnVisibilityFromUrl(hiddenColumnsParam) {
    if (!hiddenColumnsParam) return {};
    const hiddenColumns = hiddenColumnsParam.split(",").filter(Boolean);
    const visibility = {};
    hiddenColumns.forEach((columnId)=>{
        visibility[columnId.trim()] = false;
    });
    return visibility;
}
/**
 * Serialize column visibility to URL param format: "address,phone"
 * Only includes hidden columns (non-default state) for compact URLs
 * @param visibility - ColumnVisibilityState object
 * @returns Comma-separated list of hidden column IDs, or empty string if all visible
 *
 * @example
 * ```ts
 * serializeColumnVisibility({ address: false, phone: false }) // "address,phone"
 * serializeColumnVisibility({ name: true, email: true }) // ""
 * serializeColumnVisibility({}) // ""
 * ```
 */ export function serializeColumnVisibility(visibility) {
    const hiddenColumns = Object.entries(visibility).filter(([, isVisible])=>isVisible === false).map(([columnId])=>columnId);
    return hiddenColumns.join(",");
}
/**
 * Parse table state from URL parameters with optional table ID namespacing
 * @param searchParams - URL search parameters
 * @param initialPageSize - Default page size
 * @param tableId - Optional table identifier for namespacing
 * @returns Parsed table state
 *
 * @example
 * ```ts
 * parseUrlState(searchParams, 10, 'assignments')
 * // Reads from: assignments_page, assignments_sort, etc.
 *
 * parseUrlState(searchParams, 10)
 * // Reads from: page, sort, etc.
 * ```
 */ export function parseUrlState(searchParams, initialPageSize, tableId) {
    const sortParam = getPrefixedParamName("sort", tableId);
    const pageParam = getPrefixedParamName("page", tableId);
    const pageSizeParam = getPrefixedParamName("pageSize", tableId);
    const hiddenColumnsParam = getPrefixedParamName("hiddenColumns", tableId);
    return {
        sorting: searchParams[sortParam] ? parseSortingFromUrl(searchParams[sortParam]) : [],
        pageIndex: searchParams[pageParam] ? Number(searchParams[pageParam]) - 1 : 0,
        pageSize: searchParams[pageSizeParam] ? Number(searchParams[pageSizeParam]) : initialPageSize,
        columnVisibility: searchParams[hiddenColumnsParam] ? parseColumnVisibilityFromUrl(searchParams[hiddenColumnsParam]) : {}
    };
}
/**
 * Build URL params from table state
 * @param options - Options for building URL params
 * @returns URL params object
 */ export function buildUrlParams(options) {
    const { searchParams, sorting, pagination, initialPageSize, columnVisibility, tableId } = options;
    const newParams = {};
    // Get table-specific param names (the ones we own and may overwrite/remove)
    const tableParamNames = getTableParamNames(tableId);
    // Copy through unrelated params, including chip filter keys (which DataTable
    // owns separately) — anything not in our managed list passes through unchanged.
    Object.entries(searchParams).forEach(([key, value])=>{
        if (!tableParamNames.includes(key)) {
            if (typeof value === "string" || typeof value === "number") {
                newParams[key] = value;
            } else if (typeof value === "boolean") {
                // An unvalidated route JSON-parses a hand-typed `=true`; keep the
                // chip's own string form so the sync does not drop the filter.
                newParams[key] = String(value);
            }
        }
    });
    // Serialize sorting (or remove if empty)
    if (sorting.length > 0) {
        const sortParam = getPrefixedParamName("sort", tableId);
        newParams[sortParam] = serializeSorting(sorting);
    }
    // Serialize pagination (or remove if default)
    if (pagination.pageIndex > 0) {
        const pageParam = getPrefixedParamName("page", tableId);
        newParams[pageParam] = pagination.pageIndex + 1; // 1-indexed for URL
    }
    if (pagination.pageSize !== initialPageSize) {
        const pageSizeParam = getPrefixedParamName("pageSize", tableId);
        newParams[pageSizeParam] = pagination.pageSize;
    }
    // Serialize column visibility (or remove if all visible)
    if (columnVisibility && Object.keys(columnVisibility).length > 0) {
        const serialized = serializeColumnVisibility(columnVisibility);
        if (serialized) {
            const hiddenColumnsParam = getPrefixedParamName("hiddenColumns", tableId);
            newParams[hiddenColumnsParam] = serialized;
        }
    }
    return newParams;
}
/**
 * Check if URL params have changed
 * @param newParams - New params object
 * @returns True if params have changed
 */ export function hasUrlParamsChanged(newParams) {
    const currentParams = new URLSearchParams(window.location.search);
    const newParamsString = new URLSearchParams(Object.entries(newParams).filter(([, value])=>value !== undefined).map(([key, value])=>[
            key,
            String(value)
        ])).toString();
    return currentParams.toString() !== newParamsString;
}
/**
 * Format a filter value for display in active filters badges
 * Handles strings, date ranges, and number ranges
 * @param value - The filter value to format
 * @param t - Translation function (optional, for internationalization)
 * @returns Formatted string representation
 *
 * @example
 * ```ts
 * formatFilterValue("john") // "john"
 * formatFilterValue({ from: "2024-01-01", to: "2024-12-31" }) // "2024-01-01 - 2024-12-31"
 * formatFilterValue({ min: 5, max: 10 }) // "5 - 10"
 * formatFilterValue({ min: 5 }) // "≥ 5"
 * ```
 */ export function formatFilterValue(value, t) {
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "object" && value !== null) {
        // Handle date range
        if ("from" in value || "to" in value) {
            const range = value;
            if (range.from && range.to) {
                return `${range.from} - ${range.to}`;
            }
            if (range.from) {
                const prefix = t ? t("dataTable.filters.from") : "From";
                return `${prefix} ${range.from}`;
            }
            if (range.to) {
                const prefix = t ? t("dataTable.filters.to") : "To";
                return `${prefix} ${range.to}`;
            }
        }
        // Handle number range
        if ("min" in value || "max" in value) {
            const range = value;
            if (range.min !== undefined && range.max !== undefined) {
                return `${range.min} - ${range.max}`;
            }
            if (range.min !== undefined) {
                return `≥ ${range.min}`;
            }
            if (range.max !== undefined) {
                return `≤ ${range.max}`;
            }
        }
    }
    return String(value);
}
/**
 * Format a file size in bytes to a human-readable string
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB", "350 KB")
 *
 * @example
 * ```ts
 * formatFileSize(1500000) // "1.4 MB"
 * formatFileSize(500) // "500 B"
 * ```
 */ export function formatFileSize(bytes) {
    if (!bytes || bytes === 0) return "-";
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    const kb = bytes / 1024;
    if (kb < 1024) {
        return `${kb.toFixed(1)} KB`;
    }
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
}
/**
 * Format a date to a localized date string
 * @param date - Date string or Date object
 * @param locale - Locale string (defaults to the UI language, or the runtime locale before i18next is initialised)
 * @returns Formatted date string
 *
 * @example
 * ```ts
 * formatDate('2024-01-15') // "1/15/2024" (en-US) / "15.1.2024" (de-DE)
 * ```
 */ export function formatDate(date, locale = i18next.language) {
    if (!date) return "-";
    try {
        const dateObj = typeof date === "string" ? new Date(date) : date;
        return dateObj.toLocaleDateString(locale);
    } catch  {
        return "-";
    }
}
/**
 * Format a date and time to a localized string (hours and minutes, no seconds)
 * @param date - Date string or Date object
 * @param locale - Locale string (defaults to the UI language, or the runtime locale before i18next is initialised)
 * @returns Formatted date and time string
 *
 * @example
 * ```ts
 * formatDateTime('2024-01-15T10:30:00') // "1/15/2024, 10:30 AM" (en-US) / "15.1.2024, 10:30" (de-DE)
 * ```
 */ export function formatDateTime(date, locale = i18next.language) {
    if (!date) return "-";
    try {
        const dateObj = typeof date === "string" ? new Date(date) : date;
        return dateObj.toLocaleString(locale, {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    } catch  {
        return "-";
    }
}
/**
 * Format a number with locale-specific formatting
 * @param value - Number to format
 * @param locale - Locale string (defaults to 'en-US')
 * @returns Formatted number string
 *
 * @example
 * ```ts
 * formatNumber(1234567) // "1,234,567"
 * ```
 */ export function formatNumber(value, locale = "en-US") {
    if (value === undefined || value === null) return "-";
    return value.toLocaleString(locale);
}
/**
 * Format a currency value
 * @param value - Amount to format
 * @param currency - Currency code (default: 'USD')
 * @param locale - Locale string (defaults to 'en-US')
 * @returns Formatted currency string
 *
 * @example
 * ```ts
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(1234.56, 'EUR', 'de-DE') // "1.234,56 €"
 * ```
 */ export function formatCurrency(value, currency = "USD", locale = "en-US") {
    if (value === undefined || value === null) return "-";
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency
    }).format(value);
}
/**
 * Truncate text to a maximum length with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 50)
 * @returns Truncated string
 *
 * @example
 * ```ts
 * truncateText('This is a very long text that needs truncating', 20)
 * // "This is a very lo..."
 * ```
 */ export function truncateText(text, maxLength = 50) {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
}
/**
 * Render a fallback value when data is missing
 * @param value - The value to check
 * @param fallback - Fallback string (default: '-')
 * @returns The value or fallback
 *
 * @example
 * ```ts
 * renderFallback(null) // "-"
 * renderFallback('', 'N/A') // "N/A"
 * renderFallback('value') // "value"
 * ```
 */ export function renderFallback(value, fallback = "-") {
    if (value === undefined || value === null || value === "") return fallback;
    return String(value);
}
/**
 * Filter function for number range filtering
 * @param row - Table row
 * @param columnId - Column ID
 * @param filterValue - Filter value with min/max properties
 * @returns Whether the row matches the filter
 *
 * @example
 * ```ts
 * filterNumberRange(row, 'memberCount', { min: 5, max: 10 })
 * ```
 */ /**
 * Select filter for an array column: the row matches when the chosen option
 * is one of its values.
 *
 * @example
 * ```ts
 * filterArrayIncludes(row, 'userTypes', 'org_manager')
 * ```
 */ export function filterArrayIncludes(row, columnId, filterValue) {
    const values = row.getValue(columnId);
    return Array.isArray(values) && values.includes(filterValue);
}
export function filterNumberRange(row, columnId, filterValue) {
    const value = row.getValue(columnId);
    if (value === undefined || value === null) return false;
    const { min, max } = filterValue;
    if (min !== undefined && value < min) return false;
    if (max !== undefined && value > max) return false;
    return true;
}
/**
 * Filter function for date range filtering
 * @param row - Table row
 * @param columnId - Column ID
 * @param filterValue - Filter value with from/to properties (ISO date strings)
 * @returns Whether the row matches the filter
 *
 * @example
 * ```ts
 * filterDateRange(row, 'createdAt', { from: '2024-01-01', to: '2024-12-31' })
 * ```
 */ export function filterDateRange(row, columnId, filterValue) {
    const value = row.getValue(columnId);
    if (!value) return false;
    const date = typeof value === "string" ? new Date(value) : value;
    const { from, to } = filterValue;
    if (from) {
        const fromDate = new Date(from);
        if (date < fromDate) return false;
    }
    if (to) {
        const toDate = new Date(to);
        // Set to end of day for inclusive comparison
        toDate.setHours(23, 59, 59, 999);
        if (date > toDate) return false;
    }
    return true;
}
/**
 * Filter function for boolean values
 * @param row - Table row
 * @param columnId - Column ID
 * @param filterValue - Filter value as string ('true' or 'false')
 * @returns Whether the row matches the filter
 *
 * @example
 * ```ts
 * filterBoolean(row, 'isActive', 'true')
 * filterBoolean(row, 'isPublished', 'false')
 * ```
 */ export function filterBoolean(row, columnId, filterValue) {
    const value = row.getValue(columnId);
    return String(value) === filterValue;
}
/**
 * CSS Class Utilities
 */ /**
 * Get the CSS class for a font weight
 * @param fontWeight - Font weight option
 * @returns CSS class string
 *
 * @example
 * ```ts
 * getFontWeightClass('medium') // "font-medium"
 * getFontWeightClass('bold') // "font-bold"
 * ```
 */ export function getFontWeightClass(fontWeight) {
    switch(fontWeight){
        case "medium":
            return "font-medium";
        case "semibold":
            return "font-semibold";
        case "bold":
            return "font-bold";
        default:
            return "";
    }
}
/**
 * Get the CSS class for a text transform
 * @param transform - Text transform option
 * @returns CSS class string
 *
 * @example
 * ```ts
 * getTextTransformClass('uppercase') // "uppercase"
 * getTextTransformClass('capitalize') // "capitalize"
 * ```
 */ export function getTextTransformClass(transform) {
    switch(transform){
        case "uppercase":
            return "uppercase";
        case "lowercase":
            return "lowercase";
        case "capitalize":
            return "capitalize";
        default:
            return "";
    }
}

//# sourceMappingURL=utils.js.map